import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NurikabeGenerator, NurikabePuzzleData, CellState } from '../../utils/nurikabeEngine';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

const generator = new NurikabeGenerator();

export default function NurikabeGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [puzzle, setPuzzle] = useState<NurikabePuzzleData | null>(null);
  const [userGrid, setUserGrid] = useState<CellState[][]>([]);
  const [history, setHistory] = useState<CellState[][][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startTime] = useState(Date.now());
  const [moves, setMoves] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forceReload, setForceReload] = useState(0);
  const [highlightedCell, setHighlightedCell] = useState<{ r: number; c: number } | null>(null);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('nurikabe', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Board sizing
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 16);
      setBoardH(clientHeight - 16);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);

  // Load puzzle
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;
        const isNewGame = forceReload > 0;
        const data = await generator.generate(dificultadOrden, isNewGame ? undefined : seedParam);
        setPuzzle(data);
        setSearchParams({ seed: data.seed.toString() }, { replace: true });

        const empty: CellState[][] = Array(data.height).fill(0).map(() => Array(data.width).fill(-1));
        // Mark clue cells as island
        for (const key in data.clues) {
          const [r, c] = key.split(',').map(Number);
          empty[r][c] = 1;
        }
        setUserGrid(empty);
        setHistory([empty.map(row => [...row] as CellState[])]);
        setHistoryIndex(0);
        setMoves(0);
        setIsCompleted(false);
        setShowVictory(false);
        setHighlightedCell(null);
      } catch (e: any) {
        setError(e.message || 'Error al generar el puzzle');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dificultadOrden, forceReload]);

  const saveState = (newGrid: CellState[][]) => {
    const snap = newGrid.map(row => [...row] as CellState[]);
    const newHist = history.slice(0, historyIndex + 1);
    newHist.push(snap);
    if (newHist.length > 50) newHist.shift();
    setHistory(newHist);
    setHistoryIndex(newHist.length - 1);
  };

  const handleCellClick = (r: number, c: number) => {
    if (!puzzle || isCompleted) return;
    if (puzzle.clues[`${r},${c}`]) return; // clue cell, not editable

    const newGrid = userGrid.map(row => [...row] as CellState[]);
    const cur = newGrid[r][c];
    // Cycle: -1 (unknown) → 0 (wall) → 1 (island) → -1
    newGrid[r][c] = cur === -1 ? 0 : cur === 0 ? 1 : -1;
    setUserGrid(newGrid);
    setMoves(prev => prev + 1);
    saveState(newGrid);
    checkVictory(newGrid);
  };

  const checkVictory = (grid: CellState[][]) => {
    if (!puzzle || isCompleted) return;
    // Must match solution exactly
    for (let r = 0; r < puzzle.height; r++)
      for (let c = 0; c < puzzle.width; c++)
        if (grid[r][c] !== puzzle.solution[r][c]) return;

    setIsCompleted(true);
    const tiempo = Math.floor((Date.now() - startTime) / 1000);
    saveProgress(tiempo);
    setShowVictory(true);
  };

  const handleUndo = () => {
    if (historyIndex <= 0) return;
    const idx = historyIndex - 1;
    setHistoryIndex(idx);
    setUserGrid(history[idx].map(row => [...row] as CellState[]));
  };

  const handleRedo = () => {
    if (historyIndex >= history.length - 1) return;
    const idx = historyIndex + 1;
    setHistoryIndex(idx);
    setUserGrid(history[idx].map(row => [...row] as CellState[]));
  };

  const handleReset = () => {
    if (!puzzle || !window.confirm('¿Reiniciar el tablero?')) return;
    const empty: CellState[][] = Array(puzzle.height).fill(0).map(() => Array(puzzle.width).fill(-1));
    for (const key in puzzle.clues) {
      const [r, c] = key.split(',').map(Number);
      empty[r][c] = 1;
    }
    setUserGrid(empty);
    setHistory([empty.map(row => [...row] as CellState[])]);
    setHistoryIndex(0);
    setMoves(0);
    setIsCompleted(false);
    setShowVictory(false);
    setError('');
  };

  const handleHint = () => {
    if (!puzzle || isCompleted) return;

    // Priority 1: fix a wrong cell
    for (let r = 0; r < puzzle.height; r++) {
      for (let c = 0; c < puzzle.width; c++) {
        if (!puzzle.clues[`${r},${c}`] && userGrid[r][c] !== -1 && userGrid[r][c] !== puzzle.solution[r][c]) {
          const newGrid = userGrid.map(row => [...row] as CellState[]);
          newGrid[r][c] = puzzle.solution[r][c];
          setUserGrid(newGrid);
          setMoves(prev => prev + 1);
          saveState(newGrid);
          flash(r, c);
          checkVictory(newGrid);
          return;
        }
      }
    }

    // Priority 2: fill a random empty cell
    const empty: { r: number; c: number }[] = [];
    for (let r = 0; r < puzzle.height; r++)
      for (let c = 0; c < puzzle.width; c++)
        if (!puzzle.clues[`${r},${c}`] && userGrid[r][c] === -1)
          empty.push({ r, c });

    if (empty.length === 0) {
      setError('¡Ya está todo completo!');
      setTimeout(() => setError(''), 2000);
      return;
    }

    const pick = empty[Math.floor(Math.random() * empty.length)];
    const newGrid = userGrid.map(row => [...row] as CellState[]);
    newGrid[pick.r][pick.c] = puzzle.solution[pick.r][pick.c];
    setUserGrid(newGrid);
    setMoves(prev => prev + 1);
    saveState(newGrid);
    flash(pick.r, pick.c);
    checkVictory(newGrid);
  };

  const flash = (r: number, c: number) => {
    setHighlightedCell({ r, c });
    setTimeout(() => setHighlightedCell(null), 1500);
  };

  const handleSolve = () => {
    if (!puzzle || !window.confirm('¿Seguro que quieres ver la solución?')) return;
    setUserGrid(puzzle.solution.map(row => [...row] as CellState[]));
    setIsCompleted(false);
  };

  const saveProgress = async (tiempo: number) => {
    if (!puzzle) return;
    try {
      await saveGameProgress({ tablero_id: puzzle.seed, tiempo, movimientos: moves, completado: true });
      await saveSessionResult({ tiempo, movimientos: moves, completado: true, datos_juego: { tablero_id: puzzle.seed, size: puzzle.width } });
    } catch (e) {
      console.error('Error al guardar progreso:', e);
    }
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(grid: CellState[][], puz: NurikabePuzzleData): Set<string> {
    const errores = new Set<string>();
    const { height: h, width: w } = puz;
    const deltas: [number, number][] = [[-1,0],[1,0],[0,-1],[0,1]];

    // Regla 1: bloque 2×2 todo muro
    for (let r = 0; r < h - 1; r++) {
      for (let c = 0; c < w - 1; c++) {
        if (grid[r][c] === 0 && grid[r][c+1] === 0 && grid[r+1][c] === 0 && grid[r+1][c+1] === 0) {
          errores.add(`${r},${c}`); errores.add(`${r},${c+1}`);
          errores.add(`${r+1},${c}`); errores.add(`${r+1},${c+1}`);
        }
      }
    }

    // Regla 2 y 3: islas fusionadas o sobredimensionadas
    const visited = new Set<string>();
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) {
        if (grid[r][c] !== 1) continue;
        const startKey = `${r},${c}`;
        if (visited.has(startKey)) continue;

        const component: string[] = [];
        const clues: number[] = [];
        const queue: [number, number][] = [[r, c]];
        visited.add(startKey);

        while (queue.length > 0) {
          const [cr, cc] = queue.shift()!;
          const k = `${cr},${cc}`;
          component.push(k);
          if (puz.clues[k] !== undefined) clues.push(puz.clues[k]);
          for (const [dr, dc] of deltas) {
            const nr = cr + dr, nc = cc + dc;
            if (nr < 0 || nr >= h || nc < 0 || nc >= w) continue;
            const nk = `${nr},${nc}`;
            if (visited.has(nk) || grid[nr][nc] !== 1) continue;
            visited.add(nk);
            queue.push([nr, nc]);
          }
        }

        if (clues.length > 1 || (clues.length === 1 && component.length > clues[0])) {
          component.forEach(k => errores.add(k));
        }
      }
    }

    return errores;
  }

  // Cell sizing
  const cols = puzzle?.width ?? 5;
  const rows = puzzle?.height ?? 5;
  const gap = 2;
  const pad = 4;
  const cellW = boardW > 0 ? Math.floor((boardW - pad * 2 - gap * (cols - 1)) / cols) : 60;
  const cellH = boardH > 0 ? Math.floor((boardH - pad * 2 - gap * (rows - 1)) / rows) : 60;
  const fontSize = Math.floor(Math.min(cellW, cellH) * 0.42);

  const difficulties = [
    { orden: 1, nombre: 'Neurona',  icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro',  icono: '🧩' },
    { orden: 4, nombre: 'Genio',    icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-slate-900 to-blue-950 flex items-center justify-center flex-col gap-4">
        <div className="text-white text-2xl animate-pulse">Generando puzzle...</div>
        <div className="text-white/50 text-sm">Esto puede tardar unos segundos en niveles altos</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="h-full bg-gradient-to-br from-slate-900 to-blue-950 flex items-center justify-center flex-col gap-3">
        <div className="text-white text-xl">❌ {error || 'Error al cargar el puzzle'}</div>
        <button onClick={() => { setSearchParams({}); setForceReload(p => p + 1); }}
          className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30">
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-slate-900 to-blue-950 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Top bar */}
      <div className="bg-black/30 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🌊 Nurikabe</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dificultadOrden === d.orden) { setSearchParams({}); setForceReload(p => p + 1); }
                else navigate(`/juegos/nurikabe/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden ? 'bg-white text-slate-900' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={handleUndo} disabled={historyIndex <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={handleSolve} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={() => { setSearchParams({}); setForceReload(p => p + 1); }} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Board */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-2">
        <div
          className="nurikabe-board grid bg-slate-700 rounded-lg"
          style={{
            gridTemplateColumns: `repeat(${cols}, ${cellW}px)`,
            gridTemplateRows: `repeat(${rows}, ${cellH}px)`,
            gap: `${gap}px`,
            padding: `${pad}px`,
          }}
        >
          {(() => {
            const errores = modoAsistido ? calcularErrores(userGrid, puzzle) : new Set<string>();
            return Array.from({ length: rows }, (_, r) =>
            Array.from({ length: cols }, (_, c) => {

              const clueVal = puzzle.clues[`${r},${c}`];
              const isClue = clueVal !== undefined;
              const state = userGrid[r]?.[c] ?? -1;
              const isHighlighted = highlightedCell?.r === r && highlightedCell?.c === c;
              const hasError = errores.has(`${r},${c}`);

              let bgColor: string;
              let cursor = 'pointer';
              if (isHighlighted) { bgColor = '#fde047'; }
              else if (isClue)   { bgColor = '#fde68a'; cursor = 'default'; }
              else if (hasError && state === 0) { bgColor = '#7f1d1d'; }
              else if (hasError && state === 1) { bgColor = '#fecaca'; }
              else if (state === 0) { bgColor = '#1e293b'; } // muro negro
              else if (state === 1) { bgColor = '#ffffff'; } // isla blanca
              else                  { bgColor = '#64748b'; } // sin decidir

              return (
                <div
                  key={`${r}-${c}`}
                  onClick={() => handleCellClick(r, c)}
                  className="flex items-center justify-center rounded font-bold transition-colors select-none"
                  style={{ width: cellW, height: cellH, fontSize, backgroundColor: bgColor, cursor }}
                >
                  {isClue ? (
                    <span style={{ color: '#92400e', fontWeight: 'bold' }}>{clueVal}</span>
                  ) : state === 1 ? (
                    <span className="text-blue-900/30 text-xs">○</span>
                  ) : null}
                </div>
              );
            })).flat();
          })()}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 pb-2 text-xs text-white/50">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-900 inline-block border border-white/20" /> Muro (negro)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded inline-block" style={{ backgroundColor: '#fde68a', border: '1px solid #b45309' }} /> Pista</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white inline-block" /> Isla (blanco)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-500 inline-block" /> Sin decidir</span>
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Nurikabe"
        gameIcon="🌊"
        objective="Divide el tablero en islas blancas y un río negro siguiendo las reglas de tamaño y conectividad."
        rules={[
          'Cada número indica el tamaño exacto de su isla blanca (grupo de celdas blancas conectadas ortogonalmente)',
          'Cada isla contiene exactamente un número y su tamaño debe coincidir con él',
          'Todas las celdas negras forman un único río conectado (no pueden quedar aisladas)',
          'No puede haber ningún bloque de 2×2 completamente negro',
          'Las islas no pueden tocarse entre sí (solo a través del río negro)',
        ]}
        howToPlay="Toca una celda para cambiar su estado: sin decidir → negro (muro) → blanco (isla) → sin decidir. Las celdas con número son pistas fijas."
        author="Nikoli"
      />

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{ tiempo: Math.floor((Date.now() - startTime) / 1000), movimientos: moves }}
      />
    </div>
  );
}
